import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDevMode } from '../contexts/DevModeContext';
import { CheckSquare, Loader2, Search, Calendar, CheckCircle2, Plus, Clock, Trash2 } from 'lucide-react';
import axios from 'axios';
import TaskModal from './TaskModal';
import HoursModal from './HoursModal';
import FlagIcon from './FlagIcon';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyTasks = () => {
  const { user, token } = useAuth();
  const { devMode, dummyData, incrementApiCallCount } = useDevMode();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCompleted, setShowCompleted] = useState(true);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showHoursModal, setShowHoursModal] = useState(false);
  const [hoursTask, setHoursTask] = useState(null);
  const [taskOrder, setTaskOrder] = useState(() => {
    // Load saved task order from localStorage
    const saved = localStorage.getItem('myTasksOrder');
    return saved ? JSON.parse(saved) : { open: [], finished: [] };
  });

  // Check if user is admin
  const isAdmin = user?.roles?.some(role => 
    role.toLowerCase().includes('admin') || role.toLowerCase().includes('manager')
  );

  const handleTaskDragEnd = (result) => {
    const { source, destination } = result;

    // Dropped outside list or no movement
    if (!destination || 
        (source.droppableId === destination.droppableId && source.index === destination.index)) {
      return;
    }

    // Don't allow moving between open and finished
    if (source.droppableId !== destination.droppableId) {
      return;
    }

    const listType = source.droppableId === 'open-tasks' ? 'open' : 'finished';
    const sourceItems = listType === 'open' ? [...openTasksBase] : [...finishedTasksBase];
    
    // Reorder items
    const [reorderedItem] = sourceItems.splice(source.index, 1);
    sourceItems.splice(destination.index, 0, reorderedItem);
    
    // Save new order
    const newOrder = { ...taskOrder, [listType]: sourceItems.map(t => t.id) };
    setTaskOrder(newOrder);
    localStorage.setItem('myTasksOrder', JSON.stringify(newOrder));
  };

  useEffect(() => {
    if (user && token) {
      fetchTasks();
    }
  }, [user, token]);

  useEffect(() => {
    filterTasks();
  }, [tasks, searchTerm, showCompleted]);

  const fetchTasks = async () => {
    if (!user?.id) return;
    
    // Use dummy data if dev mode is enabled
    if (devMode) {
      console.log('Dev Mode: Using dummy tasks');
      setTasks(dummyData.tasks);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/tasks/user/${user.id}`, {
        headers,
        params: { rows_per_page: 1000 }
      });

      incrementApiCallCount();
      console.log('Tasks fetched:', response.data.records?.length, 'tasks');
      if (response.data.records?.[0]) {
        console.log('First task hours:', response.data.records[0].field_183_raw);
      }
      
      setTasks(response.data.records || []);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterTasks = () => {
    let filtered = [...tasks];

    if (searchTerm) {
      filtered = filtered.filter(task => {
        const name = task.field_43_raw?.toLowerCase() || '';
        const desc = task.field_44_raw?.toLowerCase() || '';
        const projectName = task.field_48_raw?.[0]?.identifier?.toLowerCase() || '';
        return name.includes(searchTerm.toLowerCase()) || 
               desc.includes(searchTerm.toLowerCase()) ||
               projectName.includes(searchTerm.toLowerCase());
      });
    }

    if (!showCompleted) {
      filtered = filtered.filter(task => !task.field_50_raw);
    }

    setFilteredTasks(filtered);
  };

  const deleteTask = async (task) => {
    // Check permissions
    const taskOwner = task.field_47_raw?.[0]?.id;
    if (!isAdmin && taskOwner !== user.id) {
      alert('You can only delete your own tasks');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${task.field_43_raw || 'this task'}"?`)) {
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/tasks/${task.id}`, { headers });
      
      // Refresh tasks after deletion
      fetchTasks();
    } catch (error) {
      console.error('Failed to delete task:', error);
      alert('Failed to delete task. Please try again.');
    }
  };

  const toggleTaskComplete = async (task) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const newCompletedStatus = !task.field_50_raw;
      
      await axios.put(`${API}/tasks/${task.id}`, {
        field_50: newCompletedStatus,
        field_50_raw: newCompletedStatus
      }, { headers });

      fetchTasks(); // Refresh tasks after update
    } catch (error) {
      console.error('Failed to update task:', error);
      alert('Failed to update task status. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Group tasks into open and finished with custom ordering
  const sortByOrder = (taskList, orderList) => {
    if (!orderList || orderList.length === 0) return taskList;
    
    const ordered = [];
    const unordered = [];
    
    taskList.forEach(task => {
      const orderIndex = orderList.indexOf(task.id);
      if (orderIndex !== -1) {
        ordered[orderIndex] = task;
      } else {
        unordered.push(task);
      }
    });
    
    return [...ordered.filter(Boolean), ...unordered];
  };

  const openTasksBase = filteredTasks.filter(task => !task.field_50_raw);
  const finishedTasksBase = filteredTasks.filter(task => task.field_50_raw);
  
  const openTasks = sortByOrder(openTasksBase, taskOrder.open);
  const finishedTasks = sortByOrder(finishedTasksBase, taskOrder.finished);

  const renderTask = (task) => (
    <>
      <div className="flex items-start gap-3 flex-1">
          <button
            onClick={() => toggleTaskComplete(task)}
            className="mt-1"
          >
            {task.field_50_raw ? (
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            ) : (
              <CheckSquare className="w-5 h-5 text-gray-400 hover:text-blue-500" />
            )}
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              {task.field_48_raw?.[0]?.identifier && (
                <div className="flex items-center gap-1">
                  {task.field_117_raw && (
                    <FlagIcon countryCode={task.field_117_raw} />
                  )}
                  <span className="text-xs text-gray-500">
                    {task.field_48_raw[0].identifier}
                  </span>
                </div>
              )}
            </div>

            <h3 className={`text-sm font-semibold mb-1 ${
              task.field_50_raw ? 'line-through text-gray-500' : 'text-gray-900'
            }`}>
              {task.field_43_raw || 'Untitled Task'}
            </h3>

            {task.field_44_raw && (
              <p className="text-xs text-gray-600 mb-2">
                {task.field_44_raw}
              </p>
            )}

            {/* Time Progress Bar */}
            {task.field_45_raw > 0 && (
              <div className="mb-3 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800 overflow-hidden">
                <div className="flex items-stretch">
                  <div className="flex-1 p-2">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-700 dark:text-gray-300 font-medium">
                        Time Progress
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">
                        {task.field_183_raw || 0}h / {task.field_45_raw}h
                        <span className="ml-1 text-blue-600 dark:text-blue-400 font-semibold">
                          ({Math.min(Math.round(((task.field_183_raw || 0) / task.field_45_raw) * 100), 100)}%)
                        </span>
                      </span>
                    </div>
                    <div className="bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all ${
                          ((task.field_183_raw || 0) / task.field_45_raw) > 1 
                            ? 'bg-red-500' 
                            : 'bg-blue-500'
                        }`}
                        style={{ width: `${Math.min(((task.field_183_raw || 0) / task.field_45_raw) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setHoursTask(task);
                      setShowHoursModal(true);
                    }}
                    className="flex items-center justify-center px-3 bg-blue-100 hover:bg-blue-200 text-blue-600 border-l border-blue-200 transition"
                    title="Register hours"
                  >
                    <Clock className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
              {task.field_46 && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  <span>Due: {task.field_46}</span>
                </div>
              )}
              {!task.field_45_raw && task.field_183_raw && (
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  <span>{task.field_183_raw}h registered</span>
                </div>
              )}
            </div>
          </div>
      </div>

      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        {!task.field_45_raw && (
          <button
            onClick={() => {
              setHoursTask(task);
              setShowHoursModal(true);
            }}
            className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-md transition"
            title="Register hours"
          >
            <Clock className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={() => deleteTask(task)}
          className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition"
          title="Delete task"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </>
  );

  return (
    <div className="space-y-6">
      <TaskModal
        isOpen={showTaskModal}
        task={selectedTask}
        onClose={() => {
          setShowTaskModal(false);
          setSelectedTask(null);
        }}
        onSuccess={() => {
          setShowTaskModal(false);
          setSelectedTask(null);
          fetchTasks();
        }}
      />

      <HoursModal
        isOpen={showHoursModal}
        task={hoursTask}
        onClose={() => {
          setShowHoursModal(false);
          setHoursTask(null);
        }}
        onSave={() => {
          setShowHoursModal(false);
          setHoursTask(null);
          fetchTasks();
        }}
      />

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">My Tasks</h1>
            <p className="text-gray-600 mt-1">
              {openTasks.length} open, {finishedTasks.length} finished
            </p>
          </div>

          <button
            onClick={() => {
              setSelectedTask(null);
              setShowTaskModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Plus className="w-5 h-5" />
            New Task
          </button>
        </div>

        <div className="mt-6 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Show completed</span>
          </label>
        </div>
      </div>

      <DragDropContext onDragEnd={handleTaskDragEnd}>
        {/* Open Tasks Section */}
        {openTasks.length > 0 && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Open Tasks ({openTasks.length})
              </h2>
            </div>
            <Droppable droppableId="open-tasks" isDropDisabled={false} isCombineEnabled={false} ignoreContainerClipping={false}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`p-6 space-y-3 ${snapshot.isDraggingOver ? 'bg-blue-50' : ''}`}
                >
                  {openTasks.map((task, index) => (
                    <Draggable key={task.id} draggableId={task.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`border border-gray-200 rounded-lg transition group ${
                            snapshot.isDragging 
                              ? 'shadow-lg border-blue-500 bg-blue-50 rotate-1' 
                              : 'hover:shadow-md hover:border-blue-200'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3 p-4">
                            <div 
                              {...provided.dragHandleProps}
                              className="pt-1 cursor-grab active:cursor-grabbing"
                              title="Drag to reorder"
                            >
                              <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
                              </svg>
                            </div>
                            {renderTask(task)}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        )}

        {/* Finished Tasks Section */}
        {showCompleted && finishedTasks.length > 0 && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Finished Tasks ({finishedTasks.length})
              </h2>
            </div>
            <Droppable droppableId="finished-tasks" isDropDisabled={false} isCombineEnabled={false} ignoreContainerClipping={false}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`p-6 space-y-3 ${snapshot.isDraggingOver ? 'bg-blue-50' : ''}`}
                >
                  {finishedTasks.map((task, index) => (
                    <Draggable key={task.id} draggableId={task.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`border border-gray-200 rounded-lg transition group ${
                            snapshot.isDragging 
                              ? 'shadow-lg border-blue-500 bg-blue-50 rotate-1' 
                              : 'hover:shadow-md hover:border-blue-200'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3 p-4">
                            <div 
                              {...provided.dragHandleProps}
                              className="pt-1 cursor-grab active:cursor-grabbing"
                              title="Drag to reorder"
                            >
                              <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
                              </svg>
                            </div>
                            {renderTask(task)}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        )}
      </DragDropContext>

      {filteredTasks.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <CheckSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks found</h3>
          <p className="text-gray-600 mb-4">Create your first task to get started</p>
          <button
            onClick={() => setShowTaskModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Plus className="w-5 h-5" />
            Create Task
          </button>
        </div>
      )}
    </div>
  );
};

export default MyTasks;
